import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  fmtTRY, fmtDate, todayISO,
  PAYMENT_STATUS_LABEL,
  CASH_ACCOUNT_LABEL, PAYMENT_METHOD_LABEL,
} from "@/lib/format";
import { PageHeader, SectionCard, StatCard, EmptyState, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Pencil, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";
import PaymentsDialog from "@/components/PaymentsDialog";
import PurchaseFormDialog from "@/components/PurchaseFormDialog";
import { generateVendorStatementPdf } from "@/lib/pdfStatement";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const EMPTY_PAYMENT = {
  purchase_id: "", date: todayISO(), amount: "",
  cash_account: "company", payment_method: "cash", notes: "",
};

export default function VendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [purOpen, setPurOpen] = useState(false);
  const [editingPur, setEditingPur] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState(EMPTY_PAYMENT);
  const [paymentsFor, setPaymentsFor] = useState(null);
  const [notesValue, setNotesValue] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const load = useCallback(async () => {
    try {
      const [v, c, j, cat] = await Promise.all([
        api.get(`/vendors/${id}`),
        api.get("/customers"),
        api.get("/jobs"),
        api.get("/categories"),
      ]);
      setData(v.data);
      setCustomers(c.data);
      setJobs(j.data);
      setCategories(cat.data);
      setNotesValue(v.data.notes || "");
    } catch {
      navigate("/vendors");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-slate-500">Yükleniyor…</div>;

  // ----- Purchase edit/delete (create is handled by PurchaseFormDialog) -----
  const onPurEdit = async (p) => {
    try {
      const { data: full } = await api.get(`/purchases/${p.id}`);
      setEditingPur(full);
      setPurOpen(true);
    } catch { toast.error("Satın alma yüklenemedi"); }
  };

  const onPurDelete = async (p) => {
    if (!window.confirm("Bu satın alma silinsin mi?")) return;
    await api.delete(`/purchases/${p.id}`);
    toast.success("Silindi");
    load();
  };

  // ----- Add payment directly from this page -----
  const onPaySubmit = async (e) => {
    e.preventDefault();
    if (!payForm.purchase_id) { toast.error("Satın alma seçin"); return; }
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) { toast.error("Geçerli tutar girin"); return; }
    try {
      await api.post(`/purchases/${payForm.purchase_id}/payments`, {
        date: payForm.date,
        amount: amt,
        cash_account: payForm.cash_account,
        payment_method: payForm.payment_method,
        notes: payForm.notes,
      });
      toast.success("Ödeme eklendi");
      setPayOpen(false);
      setPayForm(EMPTY_PAYMENT);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/vendors/${id}/notes`, { notes: notesValue });
      toast.success("Notlar güncellendi");
      load();
    } catch {
      toast.error("Notlar kaydedilemedi");
    } finally { setSavingNotes(false); }
  };

  const purchases = data.purchases || [];
  const payments = data.payments || [];

  return (
    <div className="space-y-6" data-testid="vendor-detail-page">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate("/vendors")}
          className="mb-3 -ml-2 text-slate-600" data-testid="back-to-vendors">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Tedarikçilere Dön
        </Button>
        <PageHeader title={data.name} subtitle={`${data.category || "—"} • Tedarikçi Cari Kartı`} />
      </div>

      {/* Cari kart üst bilgileri */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Tedarikçi Bilgileri" className="lg:col-span-1">
          <dl className="px-5 py-4 space-y-3 text-sm">
            <Row label="Yetkili" value={data.contact_person || "—"} />
            <Row label="Telefon" value={data.phone || "—"} />
            <Row label="E-posta" value={data.email || "—"} />
            <Row label="Kategori" value={data.category || "—"} />
            <Row label="Adres" value={data.address || "—"} />
          </dl>
        </SectionCard>

        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Toplam Satın Alma" value={fmtTRY(data.total_purchase)} accent="slate" testId="vendor-total" />
          <StatCard label="Toplam Ödeme" value={fmtTRY(data.total_paid)} accent="emerald" testId="vendor-total-paid" />
          <StatCard label="Bekleyen Borç" value={fmtTRY(data.outstanding_debt)} accent={data.outstanding_debt > 0 ? "amber" : "slate"} testId="vendor-debt" />
          <StatCard label="İşlem Sayısı" value={data.transaction_count} accent="slate" />
          <StatCard label="İlk Satın Alma" value={data.first_purchase_date ? fmtDate(data.first_purchase_date) : "—"} accent="slate" />
          <StatCard label="Son Satın Alma" value={data.last_purchase_date ? fmtDate(data.last_purchase_date) : "—"} accent="slate" />
        </div>
      </div>

      <Tabs defaultValue="purchases" className="space-y-4">
        <TabsList className="bg-white border border-slate-200 rounded-lg p-1 h-auto">
          <TabsTrigger value="purchases" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="vendor-tab-purchases">Satın Almalar</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="vendor-tab-payments">Ödemeler</TabsTrigger>
          <TabsTrigger value="movements" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="vendor-tab-movements">Cari Hareketler</TabsTrigger>
          <TabsTrigger value="notes" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="vendor-tab-notes">Notlar</TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-md data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="vendor-tab-analytics">Analiz</TabsTrigger>
        </TabsList>

        {/* TAB 1: Purchases */}
        <TabsContent value="purchases" className="space-y-4">
          <div className="flex justify-end">
            <Button
              className="bg-slate-900 hover:bg-slate-800 rounded-lg"
              onClick={() => { setEditingPur(null); setPurOpen(true); }}
              data-testid="vendor-add-purchase-button"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Yeni Satın Alma
            </Button>
          </div>

          <PurchaseFormDialog
            open={purOpen}
            onOpenChange={(v) => { setPurOpen(v); if (!v) setEditingPur(null); }}
            editing={editingPur}
            lockedVendor={data}
            customers={customers}
            jobs={jobs}
            categories={categories}
            onSaved={load}
          />

          <SectionCard testId="vendor-purchases-table">
            {(purchases ?? []).length === 0 ? (
              <EmptyState title="Bu tedarikçiden satın alma yok" hint="Yeni Satın Alma butonu ile başlayın." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/50">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">Satın Alma</th>
                      <th className="px-5 py-3 font-semibold">Tarih</th>
                      <th className="px-5 py-3 font-semibold">Müşteri</th>
                      <th className="px-5 py-3 font-semibold text-right">Tutar</th>
                      <th className="px-5 py-3 font-semibold text-right">Ödenen</th>
                      <th className="px-5 py-3 font-semibold text-right">Kalan</th>
                      <th className="px-5 py-3 font-semibold">Durum</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(purchases ?? []).map((p) => {
                      // Display-only Tutar:
                      //   Faturalı  → SUM(purchase'a bağlı invoice.grand_total) — KDV DAHİL
                      //   Faturasız → purchase.amount (mevcut) — KDV yok
                      // ``purchase.amount`` (KDV hariç maliyet) tabloda GÖRÜNMEZ ancak
                      // backend'de değişmez; kârlılık/KDV/tedarikçi borç hesaplarını etkilemez.
                      const isInvoiced = (p.invoice_status || "invoiced") === "invoiced";
                      const displayTutar = isInvoiced
                        ? (Number(p.invoice_grand_total_sum) || 0)
                        : (Number(p.amount) || 0);
                      const kalan = displayTutar - (Number(p.paid_amount) || 0);
                      return (
                        <tr key={p.id} className="hover:bg-slate-50/60" data-testid={`vendor-purchase-row-${p.id}`}>
                          <td className="px-5 py-3">
                            <button
                              type="button"
                              onClick={() => onPurEdit(p)}
                              className="text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 rounded"
                              data-testid={`vendor-purchase-title-${p.id}`}
                              title="Satın almayı düzenle"
                            >
                              <div className="font-medium text-slate-900 group-hover:underline cursor-pointer">{p.description || "—"}</div>
                              <div className="text-xs text-slate-500">{p.category || "—"}</div>
                            </button>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap">{fmtDate(p.date)}</td>
                          <td className="px-5 py-3 font-medium hover:underline cursor-pointer" onClick={() => navigate(`/customers/${p.customer_id}`)}>{p.customer_name}</td>
                          <td className="px-5 py-3 text-right tabular-nums"
                              data-testid={`vendor-purchase-tutar-${p.id}`}>
                            {fmtTRY(displayTutar)}
                            {isInvoiced && (
                              <span
                                className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium
                                           bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider align-middle"
                                title="KDV Dahil — Faturaların Genel Toplamı"
                                data-testid={`vendor-purchase-tutar-vat-badge-${p.id}`}
                              >
                                KDV
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-right tabular-nums text-emerald-600">{fmtTRY(p.paid_amount)}</td>
                          <td className="px-5 py-3 text-right tabular-nums font-semibold">{fmtTRY(kalan)}</td>
                          <td className="px-5 py-3"><StatusBadge value={p.payment_status} label={PAYMENT_STATUS_LABEL[p.payment_status]} /></td>
                          <td className="px-5 py-3">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => setPaymentsFor(p)} data-testid={`vendor-purchase-payments-${p.id}`}>Ödemeler</Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onPurEdit(p)} data-testid={`vendor-purchase-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => onPurDelete(p)} data-testid={`vendor-purchase-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* TAB 2: Payments */}
        <TabsContent value="payments" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={payOpen} onOpenChange={(v) => { setPayOpen(v); if (!v) setPayForm(EMPTY_PAYMENT); }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 rounded-lg" disabled={(purchases ?? []).length === 0} data-testid="vendor-add-payment-button">
                  <Plus className="w-4 h-4 mr-1.5" /> Yeni Ödeme
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle className="font-display">Yeni Ödeme Ekle</DialogTitle></DialogHeader>
                <form onSubmit={onPaySubmit} className="space-y-4" data-testid="vendor-payment-form">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Satın Alma *</Label>
                    <Select value={payForm.purchase_id} onValueChange={(v) => setPayForm({ ...payForm, purchase_id: v })}>
                      <SelectTrigger className="h-10 rounded-lg" data-testid="vendor-payment-purchase-select"><SelectValue placeholder="Hangi satın alma için?" /></SelectTrigger>
                      <SelectContent>
                        {(purchases ?? []).filter(p => (+p.amount - +p.paid_amount) > 0.01).map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {fmtDate(p.date)} • {p.description || p.category || "Satın Alma"} • Kalan {fmtTRY(p.amount - p.paid_amount)}
                          </SelectItem>
                        ))}
                        {(purchases ?? []).filter(p => (+p.amount - +p.paid_amount) > 0.01).length === 0 && (
                          <div className="px-3 py-2 text-xs text-slate-500">Bekleyen borçlu satın alma yok</div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Tarih *" type="date" value={payForm.date} onChange={(v) => setPayForm({ ...payForm, date: v })} required testId="vendor-payment-date-input" />
                    <Field label="Tutar (₺) *" type="number" step="0.01" value={payForm.amount} onChange={(v) => setPayForm({ ...payForm, amount: v })} required testId="vendor-payment-amount-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Kasa</Label>
                      <Select value={payForm.cash_account} onValueChange={(v) => setPayForm({ ...payForm, cash_account: v })}>
                        <SelectTrigger className="h-10 rounded-lg" data-testid="vendor-payment-cash-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="company">Şirket Kasası</SelectItem>
                          <SelectItem value="personal">Şahsi Kasa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Ödeme Yöntemi</Label>
                      <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                        <SelectTrigger className="h-10 rounded-lg" data-testid="vendor-payment-method-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(PAYMENT_METHOD_LABEL ?? {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Notlar</Label>
                    <Textarea value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} rows={2} />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setPayOpen(false)}>Vazgeç</Button>
                    <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="vendor-payment-save-button">Kaydet</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <SectionCard title={`Tüm Ödemeler (${(payments ?? []).length})`} testId="vendor-payments-card">
            {(payments ?? []).length === 0 ? (
              <EmptyState title="Henüz ödeme kaydı yok" hint="Ödemeler Satın Almalar sekmesinden de eklenebilir." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/50">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">Tarih</th>
                      <th className="px-5 py-3 font-semibold">Satın Alma</th>
                      <th className="px-5 py-3 font-semibold text-right">Tutar</th>
                      <th className="px-5 py-3 font-semibold">Yöntem</th>
                      <th className="px-5 py-3 font-semibold">Kasa</th>
                      <th className="px-5 py-3 font-semibold">Notlar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(payments ?? []).map((p) => (
                      <tr key={p.id}>
                        <td className="px-5 py-3 whitespace-nowrap">{fmtDate(p.date)}</td>
                        <td className="px-5 py-3 font-medium">{p.purchase_description}</td>
                        <td className="px-5 py-3 text-right tabular-nums font-semibold text-emerald-600">{fmtTRY(p.amount)}</td>
                        <td className="px-5 py-3 text-xs">{PAYMENT_METHOD_LABEL[p.payment_method]}</td>
                        <td className="px-5 py-3 text-xs">{CASH_ACCOUNT_LABEL[p.cash_account]}</td>
                        <td className="px-5 py-3 text-xs text-slate-600">{p.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* TAB 3: Cari Hareketler */}
        <TabsContent value="movements">
          <VendorMovementsTab vendor={data} purchases={purchases} payments={payments} />
        </TabsContent>

        {/* TAB 4: Notes */}
        <TabsContent value="notes">
          <SectionCard title="Tedarikçi Notları & İletişim Geçmişi">
            <div className="p-5 space-y-3">
              <Textarea value={notesValue} onChange={(e) => setNotesValue(e.target.value)} rows={10} className="rounded-lg" placeholder="Tedarikçi ile ilgili notlar, iletişim geçmişi, sözleşme detayları..." data-testid="vendor-notes-textarea" />
              <div className="flex justify-end">
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={saveNotes} disabled={savingNotes} data-testid="vendor-save-notes-button">
                  {savingNotes ? "Kaydediliyor..." : "Notları Kaydet"}
                </Button>
              </div>
            </div>
          </SectionCard>
        </TabsContent>

        {/* TAB 5: Analytics */}
        <TabsContent value="analytics">
          <VendorAnalyticsTab data={data} purchases={purchases} payments={payments} />
        </TabsContent>
      </Tabs>

      <PaymentsDialog
        key={paymentsFor?.id}
        open={!!paymentsFor}
        onOpenChange={(v) => { if (!v) setPaymentsFor(null); }}
        type="purchase"
        record={paymentsFor}
        onUpdated={() => load()}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</dt>
      <dd className="col-span-2 text-slate-800 break-words">{value}</dd>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", step, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={value || ""} type={type} step={step} onChange={(e) => onChange(e.target.value)} required={required} className="h-10 rounded-lg" data-testid={testId} />
    </div>
  );
}

function VendorMovementsTab({ vendor, purchases, payments }) {
  const txns = [];
  for (const p of purchases) {
    // Faturalı satın almada BORÇ tutarı KDV DAHİL fatura toplamı olur;
    // ``purchase.amount`` (KDV hariç maliyet) cari hesap hareketi için
    // yanlış bir base'dir.  Backend'in ``get_vendor`` endpoint'inde
    // eklenen display-only ``invoice_grand_total_sum`` alanı burada
    // kullanılır (iter39).  Faturasız satın almada mevcut davranış
    // korunur.
    const isInvoiced = (p.invoice_status || "invoiced") === "invoiced";
    const debit = isInvoiced
      ? (Number(p.invoice_grand_total_sum) || 0)
      : (Number(p.amount) || 0);
    txns.push({
      key: `pur-${p.id}`,
      date: p.date,
      type: "purchase",
      description: p.description || p.category || "Satın Alma",
      debit,
      credit: 0,
      vatBadge: isInvoiced,
    });
  }
  for (const pm of payments) {
    txns.push({
      key: `pay-${pm.id}`,
      date: pm.date,
      type: "payment",
      description: `Ödeme — ${pm.purchase_description}${pm.notes ? ` (${pm.notes})` : ""}`,
      debit: 0,
      credit: +pm.amount || 0,
    });
  }
  txns.sort((a, b) => {
    const da = a.date || "";
    const db = b.date || "";
    if (da !== db) return da < db ? -1 : 1;
    if (a.type === b.type) return 0;
    return a.type === "purchase" ? -1 : 1;
  });

  let running = 0;
  const rows = (txns ?? []).map((t) => {
    running += t.debit - t.credit;
    return { ...t, balance: running };
  });

  const totalDebit = (rows ?? []).reduce((s, r) => s + r.debit, 0);
  const totalCredit = (rows ?? []).reduce((s, r) => s + r.credit, 0);
  const finalBalance = totalDebit - totalCredit;

  const onDownloadPdf = () => {
    if ((rows ?? []).length === 0) {
      toast.error("Henüz cari hareket yok, PDF oluşturulamaz");
      return;
    }
    try {
      generateVendorStatementPdf({ vendor, rows, totalDebit, totalCredit, finalBalance });
      toast.success("PDF cari ekstresi oluşturuldu");
    } catch (err) {
      console.error(err);
      toast.error("PDF oluşturulamadı");
    }
  };

  return (
    <SectionCard
      title={`Cari Hareket Dökümü (${(rows ?? []).length})`}
      testId="vendor-movements-card"
      action={
        <Button
          size="sm"
          variant="outline"
          className="h-9 rounded-lg"
          onClick={onDownloadPdf}
          disabled={(rows ?? []).length === 0}
          data-testid="vendor-download-statement-pdf-button"
        >
          <FileDown className="w-4 h-4 mr-1.5" /> PDF Cari Ekstresi Oluştur
        </Button>
      }
    >
      {(rows ?? []).length === 0 ? (
        <EmptyState title="Henüz cari hareket yok" hint="Satın almalar ve ödemeler bu sekmede kronolojik olarak listelenir." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Tarih</th>
                <th className="px-5 py-3 font-semibold">İşlem Tipi</th>
                <th className="px-5 py-3 font-semibold">Açıklama</th>
                <th className="px-5 py-3 font-semibold text-right">Borç</th>
                <th className="px-5 py-3 font-semibold text-right">Alacak</th>
                <th className="px-5 py-3 font-semibold text-right">Bakiye</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(rows ?? []).map((r) => (
                <tr key={r.key} className="hover:bg-slate-50/60" data-testid={`vendor-movement-row-${r.key}`}>
                  <td className="px-5 py-3 whitespace-nowrap">{r.date ? fmtDate(r.date) : "—"}</td>
                  <td className="px-5 py-3">
                    <StatusBadge value={r.type === "purchase" ? "invoiced" : "paid"} label={r.type === "purchase" ? "Satın Alma" : "Ödeme"} />
                  </td>
                  <td className="px-5 py-3 text-slate-700 max-w-md truncate">{r.description || "—"}</td>
                  <td className="px-5 py-3 text-right tabular-nums" data-testid={`vendor-movement-debit-${r.key}`}>
                    {r.debit > 0 ? (
                      <span className="text-slate-900 inline-flex items-center gap-1.5 justify-end">
                        {fmtTRY(r.debit)}
                        {r.vatBadge && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium
                                       bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider"
                            title="KDV Dahil — Faturaların Genel Toplamı"
                            data-testid={`vendor-movement-debit-vat-badge-${r.key}`}
                          >
                            KDV
                          </span>
                        )}
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {r.credit > 0 ? <span className="text-emerald-600">{fmtTRY(r.credit)}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className={`px-5 py-3 text-right tabular-nums font-semibold ${r.balance > 0 ? "text-amber-700" : r.balance < 0 ? "text-emerald-700" : "text-slate-500"}`}>
                    {fmtTRY(r.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr className="border-t-2 border-slate-200 font-semibold">
                <td className="px-5 py-3" colSpan={3}>
                  <span className="text-[11px] uppercase tracking-wider text-slate-600">Toplam</span>
                </td>
                <td className="px-5 py-3 text-right tabular-nums text-slate-900" data-testid="vendor-movements-total-debit">{fmtTRY(totalDebit)}</td>
                <td className="px-5 py-3 text-right tabular-nums text-emerald-700" data-testid="vendor-movements-total-credit">{fmtTRY(totalCredit)}</td>
                <td className={`px-5 py-3 text-right tabular-nums ${finalBalance > 0 ? "text-amber-700" : finalBalance < 0 ? "text-emerald-700" : "text-slate-500"}`} data-testid="vendor-movements-final-balance">
                  {fmtTRY(finalBalance)}
                </td>
              </tr>
              <tr className="text-xs text-slate-500">
                <td className="px-5 pb-3" colSpan={6}>
                  Pozitif bakiye: tedarikçiye olan borç. Negatif bakiye: avans / fazla ödeme.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

function VendorAnalyticsTab({ data, purchases }) {
  // Monthly spending: group purchases by YYYY-MM and sum amount
  const monthly = useMemo(() => {
    const map = new Map();
    for (const p of purchases) {
      if (!p.date) continue;
      const ym = p.date.slice(0, 7); // YYYY-MM
      map.set(ym, (map.get(ym) || 0) + (+p.amount || 0));
    }
    const arr = Array.from(map.entries())
      .map(([ym, amount]) => {
        const [y, m] = ym.split("-");
        return { ym, label: `${m}.${y}`, amount };
      })
      .sort((a, b) => (a.ym < b.ym ? -1 : 1));
    return arr;
  }, [purchases]);

  const avgMonthly = (monthly ?? []).length > 0
    ? (monthly ?? []).reduce((s, m) => s + m.amount, 0) / (monthly ?? []).length
    : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Toplam Satın Alma" value={fmtTRY(data.total_purchase)} accent="slate" testId="analytics-total-purchase" />
        <StatCard label="Toplam Ödeme" value={fmtTRY(data.total_paid)} accent="emerald" />
        <StatCard label="Bekleyen Borç" value={fmtTRY(data.outstanding_debt)} accent={data.outstanding_debt > 0 ? "amber" : "slate"} />
        <StatCard label="Aylık Ortalama" value={fmtTRY(avgMonthly)} accent="slate" testId="analytics-avg-monthly" />
      </div>

      <SectionCard title="Aylık Satın Alma Harcaması" testId="vendor-monthly-chart">
        <div className="p-5">
          {(monthly ?? []).length === 0 ? (
            <EmptyState title="Aylık veri yok" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthly} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" stroke="#94a3b8" axisLine={false} tickLine={false} />
                <YAxis stroke="#94a3b8" axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => fmtTRY(v)} />
                <Bar dataKey="amount" name="Harcama" fill="#0f172a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
